/**
 * Tesseramento (V08): numero e periodo. Chiuderlo significa dargli una fine.
 * Lo usano la rosa e la scheda giocatore, sulle stesse rotte.
 */

import { type FormEvent, useState } from 'react';
import { modificaTesseramentoSchema } from '@calcio/shared';
import { OctagonXIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { erroriPerCampo, messaggio } from '../comuni/dati.js';
import { oggi } from './elenchi.js';
import { CampoData } from '../comuni/CampoData.js';

export function FormTesseramento({
  ss,
  tesseramentoId,
  nome,
  cognome,
  numeroMaglia,
  dataInizio,
  dataFine,
  onChiudi,
  onSalvato,
}: {
  ss: string;
  tesseramentoId: string;
  nome: string;
  cognome: string;
  numeroMaglia: number | null;
  dataInizio: string;
  dataFine: string | null;
  onChiudi: () => void;
  onSalvato: () => Promise<void>;
}) {
  const [valori, setValori] = useState({
    numeroMaglia: numeroMaglia?.toString() ?? '',
    dataInizio,
    dataFine: dataFine ?? '',
  });
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const salva = async (fine: string) => {
    const esito = modificaTesseramentoSchema.safeParse({
      numeroMaglia: valori.numeroMaglia === '' ? null : Number(valori.numeroMaglia),
      dataInizio: valori.dataInizio,
      dataFine: fine,
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(`/squadre-stagione/${ss}/tesseramenti/${tesseramentoId}`, {
        metodo: 'PUT',
        corpo: esito.data,
      });
      await onSalvato();
      toast.success('Tesseramento salvato.');
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  const campoData = (nomeCampo: 'dataInizio' | 'dataFine', etichetta: string) => (
    <Field data-invalid={Boolean(errori[nomeCampo])}>
      <FieldLabel htmlFor={`tesseramento-${nomeCampo}`}>{etichetta}</FieldLabel>
      <CampoData
        id={`tesseramento-${nomeCampo}`}
        valore={valori[nomeCampo]}
        // La fine non precede l'inizio, e si toglie per riaprire il tesseramento.
        min={nomeCampo === 'dataFine' ? valori.dataInizio || undefined : undefined}
        cancellabile={nomeCampo === 'dataFine'}
        segnaposto={nomeCampo === 'dataFine' ? 'In corso' : undefined}
        invalido={Boolean(errori[nomeCampo])}
        onCambia={(data) => setValori((v) => ({ ...v, [nomeCampo]: data }))}
      />
      <FieldError>{errori[nomeCampo]}</FieldError>
    </Field>
  );

  const campo = (nomeCampo: keyof typeof valori, etichetta: string, tipo: string) => (
    <Field data-invalid={Boolean(errori[nomeCampo])}>
      <FieldLabel htmlFor={`tesseramento-${nomeCampo}`}>{etichetta}</FieldLabel>
      <Input
        id={`tesseramento-${nomeCampo}`}
        type={tipo}
        value={valori[nomeCampo]}
        aria-invalid={Boolean(errori[nomeCampo])}
        onChange={(e) => setValori((v) => ({ ...v, [nomeCampo]: e.target.value }))}
      />
      <FieldError>{errori[nomeCampo]}</FieldError>
    </Field>
  );

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void salva(valori.dataFine);
      }}
      className="flex flex-col gap-6"
    >
      <DialogHeader>
        <DialogTitle>
          {cognome} {nome}
        </DialogTitle>
        <DialogDescription>
          Senza data di fine il tesseramento resta aperto. La fine è l'ultimo giorno in rosa.
        </DialogDescription>
      </DialogHeader>

      <FieldGroup>
        {campo('numeroMaglia', 'Numero di maglia', 'number')}
        <div className="grid grid-cols-2 gap-4">
          {campoData('dataInizio', 'Inizio')}
          {campoData('dataFine', 'Fine')}
        </div>
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        {!dataFine && (
          <Button
            type="button"
            variant="destructive"
            className="sm:mr-auto"
            onClick={() => void salva(oggi())}
            disabled={inCorso}
          >
            Chiudi tesseramento
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          Salva
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
